import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { ValidationError } from "yup";
import {
  InputWithLabel,
  PrimaryButton,
  Select,
  TextInput,
} from "@normasteaching/norma-ui";
import { yupResolver } from "@hookform/resolvers/yup";
import { italianProvinces } from "./italianProvinces.ts";
import { PropsWithChildren, useState } from "react";
import CodiceFiscale from "codice-fiscale-js";
import { BusinessType, businessTypeLabels } from "./BusinessType.tsx";
import { sanitizeNotVisibleUnicodeCharacters } from "./sanitizeNotVisibleUnicodeCharacters.tsx";

export type InvoiceMoneyTransferFormParams = {
  email: string;
  firstName: string;
  lastName: string;
  businessType: BusinessType;
  businessName?: string;
  address: {
    street: string;
    city: string;
    cap: string;
    province: string;
  };
  sdiCode?: string;
  pec?: string;
  taxCode: string;
  vat?: string;
};
function FormRow({ children }: PropsWithChildren) {
  return (
    <div
      className={
        "flex flex-col space-y-4 md:flex-row md:space-x-4 md:space-y-0"
      }
    >
      {children ?? null}
    </div>
  );
}

export function InvoiceMoneyTransferForm({
  onSubmit,
}: {
  onSubmit: (data: InvoiceMoneyTransferFormParams) => Promise<void>;
}) {
  const [creatingPaymentIntent, setCreatingPaymentIntent] = useState(false);
  const schema = yup
    .object({
      email: yup
        .string()
        .email("Specifica un'email valida")
        .required("Inserisci un'email"),
      firstName: yup.string().required("Inserisci il tuo nome"),
      lastName: yup.string().required("Inserisci il tuo cognome"),
      businessType: yup
        .mixed<BusinessType>()
        .oneOf(Object.values(BusinessType) as BusinessType[])
        .required("Specifica la tipologia di attività"),
      businessName: yup.string().when("businessType", {
        is: (type: BusinessType) => [BusinessType.Company].includes(type),
        then: (yup) => {
          return yup.required("Inserisci la ragione sociale");
        },
      }),
      vat: yup.string().when("businessType", {
        is: (type: BusinessType) =>
          [BusinessType.Company, BusinessType.IndividualCompany].includes(type),
        then: (yup) => {
          return yup.required("Inserisci la partita iva");
        },
      }),
      pec: yup.string().email("Specifica una PEC valida"),
      taxCode: yup
        .string()
        .required("Inserisci il codice fiscale")
        .when("businessType", {
          is: (type: BusinessType) =>
            [BusinessType.Person, BusinessType.IndividualCompany].includes(
              type,
            ),
          then: (yup) => {
            return yup.test(
              "is-valid-cf",
              "Codice fiscale non valido",
              (value) => {
                if (!value) return false;
                return CodiceFiscale.check(value);
              },
            );
          },
        }),
      address: yup.object({
        street: yup.string().required("Inserisci la via"),
        city: yup.string().required("Inserisci la città"),
        cap: yup.string().required("Inserisci il cap"),
        province: yup.string().required("Inserisci la provincia"),
      }),
      sdiCode: yup.string(),
    })
    .test(
      "has-at-least-sdi-or-pec",
      "Inserisci almeno un codice SDI o PEC",
      (value, { createError }) => {
        if (
          ![BusinessType.Company, BusinessType.IndividualCompany].includes(
            value.businessType,
          )
        )
          return true;
        const isValid = !!value.sdiCode || !!value.pec;
        if (!isValid) {
          const error = "Inserisci almeno un codice SDI o PEC";
          return createError({
            message: () => [
              new ValidationError(error, undefined, "sdiCode"),
              new ValidationError(error, undefined, "pec"),
            ],
          });
        }
        return isValid;
      },
    );
  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
    trigger,
  } = useForm<InvoiceMoneyTransferFormParams>({
    resolver: yupResolver(schema),
    mode: "onChange",
  });

  const businessType = watch("businessType");
  async function submitForm(data: InvoiceMoneyTransferFormParams) {
    try {
      setCreatingPaymentIntent(true);
      await onSubmit(data);
    } finally {
      setCreatingPaymentIntent(false);
    }
  }

  const companyOrIndividualCompany = [
    BusinessType.Company,
    BusinessType.IndividualCompany,
  ];

  const sdiCodeControl = register("sdiCode");
  const pecControl = register("pec");

  function renderTaxCodePlaceholder(businessType?: BusinessType) {
    switch (businessType) {
      case BusinessType.Person:
      case BusinessType.IndividualCompany:
        return "Il tuo codice fiscale personale";
      case BusinessType.Company:
        return "Il codice fiscale della tua azienda";
      default:
        return "Codice Fiscale";
    }
  }

  return (
    <form className={"flex flex-col space-y-4"}>
      <InputWithLabel
        subLabel={
          "Se si tratta di un rinnovo, inserisci l'email usata per l'iscrizione"
        }
        label={"Email"}
      >
        <TextInput
          errorMessage={errors.email?.message}
          type={"email"}
          placeholder={"La tua email"}
          {...register("email")}
        />
      </InputWithLabel>
      <FormRow>
        <InputWithLabel label={"Nome"}>
          <TextInput
            placeholder={"Il tuo nome"}
            {...register("firstName")}
            errorMessage={errors.firstName?.message}
          />
        </InputWithLabel>
        <InputWithLabel label={"Cognome"}>
          <TextInput
            placeholder={"Il tuo cognome"}
            {...register("lastName")}
            errorMessage={errors.lastName?.message}
          />
        </InputWithLabel>
      </FormRow>
      <FormRow>
        <InputWithLabel label={"Indirizzo"}>
          <TextInput
            placeholder={"Il tuo indirizzo"}
            {...register("address.street")}
            errorMessage={errors.address?.street?.message}
          />
        </InputWithLabel>
        <InputWithLabel label={"Città"}>
          <TextInput
            placeholder={"La tua città"}
            {...register("address.city")}
            errorMessage={errors.address?.city?.message}
          />
        </InputWithLabel>
      </FormRow>
      <FormRow>
        <InputWithLabel label={"Cap"}>
          <TextInput
            placeholder={"CAP"}
            {...register("address.cap")}
            errorMessage={errors.address?.cap?.message}
          />
        </InputWithLabel>
        <InputWithLabel label={"Provincia"}>
          <Controller
            render={({ field }) => {
              return (
                <Select
                  searchable
                  placeholder={"Seleziona la provincia"}
                  value={field.value}
                  onChange={field.onChange}
                  errorMessage={errors.address?.province?.message}
                  options={italianProvinces.map((p) => ({
                    name: p.nome,
                    id: p.sigla,
                  }))}
                />
              );
            }}
            name={"address.province"}
            control={control}
          />
        </InputWithLabel>
      </FormRow>
      <FormRow>
        <InputWithLabel label={"Codice Fiscale"}>
          <Controller
            render={({ field }) => (
              <TextInput
                placeholder={renderTaxCodePlaceholder(businessType)}
                testId={"taxCode"}
                value={field.value}
                onChange={(v) => {
                  field.onChange(v.target.value?.toUpperCase());
                }}
                errorMessage={errors.taxCode?.message}
              />
            )}
            name={"taxCode"}
            control={control}
          />
        </InputWithLabel>
        <InputWithLabel label={"Tipologia di attività"}>
          <Controller
            render={({ field }) => {
              return (
                <Select
                  testId={"businessType"}
                  placeholder={"Seleziona la tipologia di attività"}
                  value={field.value}
                  onChange={async (v) => {
                    field.onChange(v);
                    await trigger();
                  }}
                  errorMessage={errors.businessType?.message}
                  options={Object.values(BusinessType).map((type) => ({
                    id: type,
                    name: businessTypeLabels[type],
                  }))}
                />
              );
            }}
            name={"businessType"}
            control={control}
          />
        </InputWithLabel>
      </FormRow>
      <FormRow>
        <InputWithLabel label={"Ragione sociale"}>
          <TextInput
            errorMessage={errors.businessName?.message}
            disabled={businessType !== BusinessType.Company}
            placeholder={"Ragione Sociale"}
            {...register("businessName")}
          />
        </InputWithLabel>
        <InputWithLabel label={"Codice SDI"}>
          <TextInput
            testId={"sdiCode"}
            errorMessage={errors.sdiCode?.message}
            disabled={!companyOrIndividualCompany.includes(businessType)}
            placeholder={"Codice SDI"}
            {...sdiCodeControl}
            onChange={async (v) => {
              await sdiCodeControl.onChange(v);
              await trigger(["pec", "sdiCode"]);
            }}
          />
        </InputWithLabel>
      </FormRow>
      <FormRow>
        <InputWithLabel label={"PEC"}>
          <TextInput
            testId={"pec"}
            errorMessage={errors.pec?.message}
            disabled={!companyOrIndividualCompany.includes(businessType)}
            placeholder={"PEC"}
            {...pecControl}
            onChange={async (v) => {
              await pecControl.onChange(v);
              await trigger(["pec", "sdiCode"]);
            }}
          />
        </InputWithLabel>
        <InputWithLabel label={"Partita IVA"}>
          <Controller
            render={({ field }) => {
              return (
                <TextInput
                  errorMessage={errors.vat?.message}
                  disabled={!companyOrIndividualCompany.includes(businessType)}
                  placeholder={"Partita IVA"}
                  testId={"vat"}
                  value={field.value}
                  onChange={(v) => {
                    field.onChange(
                      sanitizeNotVisibleUnicodeCharacters(
                        v.target.value ?? "",
                      ).trim(),
                    );
                  }}
                />
              );
            }}
            control={control}
            name={"vat"}
          />
        </InputWithLabel>
      </FormRow>
      <div className={"flex flex-col space-y-1"}>
        <PrimaryButton
          testId={"submit-button"}
          loading={creatingPaymentIntent}
          label={"Ottieni informazioni"}
          onClick={handleSubmit(submitForm)}
        />
        <p className={"text-slate-500 text-sm"}>
          Spese e commissioni per bonifici esteri interamente a carico
          dell'ordinante
        </p>
      </div>
    </form>
  );
}
